<template>
  <div class="overflow-y-auto max-w-[360px] flex flex-col gap-3">
    <div class="text-h6 font-bold">Signer</div>

    <PreposAccordion v-if="signaturePositionsStore.unlinked.length" :list="signaturePositionsStore.unlinked" short />

    <PendingSignAccordion v-if="pendingSignatures.length" />

    <div v-if="!userPrefStore.defaultSignature" class="text-center">
      Votre profil ne contient aucune signature. Veuillez en ajouter afin de pouvoir signer ce document.
      <PreferencesForm defaultTab="signatures">
        <template #activator="{ open }">
          <PButton mode="primary" class="mt-3 w-full gap-1" @click="open">
            <OhVueIcon :name="addIcon" />
            Ajouter ma signature
          </PButton>
        </template>
      </PreferencesForm>
    </div>

    <template v-else>
      <div class="border border-grey-850 p-1">
        <div class="flex flex-row">
          <img
            :src="defaultSignatureLink!"
            :alt="`Signature ${userPrefStore.defaultSignature.id}`"
            class="max-w-[110px] max-h-[110px]"
          />
          <EnhanceSign :id="userPrefStore.defaultSignature.id" :disabled="signPlacementOngoing" />
        </div>
        <PreferencesForm defaultTab="signatures">
          <template #activator="{ open }">
            <PButton mode="tertiary" size="sm" class="mt-2 w-full gap-1" @click="open" :disabled="signPlacementOngoing">
              Choisir une autre signature
            </PButton>
          </template>
        </PreferencesForm>
      </div>

      <PButton mode="secondary" @click="placeSignature" :loading="signPlacementOngoing"> Apposer ma signature </PButton>
      <PButton
        mode="primary"
        @click="chooseSignMode"
        :disabled="!pendingSignatures.length || signPlacementOngoing || signing"
        :loading="signing"
        class="-mt-1"
      >
        Signer
        <span v-if="simpleFeatureStore.pades.enabled" class="ml-1 font-normal">(simple ou renforcée)</span>
      </PButton>
      <PFormMessage
        v-if="pendingSignatures.length > 0"
        :infoMessages="['Signer implique la fermeture du document. Vous pourrez le rouvrir par la suite.']"
        class="-mt-2!"
      />
    </template>

    <SignModeChoiceModal v-model="signModeChoiceOpen" :loading="signing" :locked="signModeLocked" @sign="sign" />
  </div>
</template>

<script setup lang="ts">
import PreferencesForm from "@/app/components/PreferencesForm.vue";
import { type SCWSApi, useSCWS } from "@/app/composable/useSCWS.ts";
import { useLetterFileDocumentApi } from "@/app/http/letter-file-document.api.ts";
import { useLetterFileStore } from "@/app/stores/letter-file.store.ts";
import { useLoadingStore } from "@/app/stores/loading.store.ts";
import { useSignaturePositionStore } from "@/app/stores/signature-position.store.ts";
import { useSimpleFeatureStore } from "@/app/stores/simple-features.store.ts";
import { useUserPreferencesStore } from "@/app/stores/user-preferences.store.ts";
import { replacePlural } from "@/app/utils/plural.ts";
import EnhanceSign from "@/app/views/LetterFileForm/Sign/EnhanceSign.vue";
import type { PendingSignature } from "@/app/views/PdfViewer/domain/pending-signature.domain.ts";
import SignModeChoiceModal from "@/app/views/PdfViewer/SignModeChoiceModal.vue";
import PendingSignAccordion from "@/app/views/PdfViewer/tools/PendingSignAccordion.vue";
import PreposAccordion from "@/app/views/PdfViewer/tools/PreposAccordion.vue";
import {
  type Alert,
  AlertDuration,
  AlertType,
  PButton,
  PFormMessage,
  useAlertManager,
  useIcons,
  usePastaRouter,
} from "@pasta/box-v2";
import { useSentry } from "@pasta/front-sentry-v2";
import { uniq } from "lodash";
import { OhVueIcon } from "oh-vue-icons";
import { RiAddFill } from "oh-vue-icons/icons";
import { computed, ref, watch } from "vue";
import { onBeforeRouteLeave } from "vue-router";
import { useSignEvents } from "../composable/use-sign-events.ts";

type Props = { idDocToSign: number; pendingSignatures: PendingSignature[] };
const props = defineProps<Props>();

const router = usePastaRouter();
const simpleFeatureStore = useSimpleFeatureStore();
const userPrefStore = useUserPreferencesStore();
const letterFileStore = useLetterFileStore();
const letterFile = computed(() => {
  return letterFileStore.getCurrent();
});
const documentApi = useLetterFileDocumentApi();
const signaturePositionsStore = useSignaturePositionStore();
const loadingStore = useLoadingStore();
const signEvents = useSignEvents();
const sentry = useSentry();
const alertManager = useAlertManager();
const scws = useSCWS();

const defaultSignatureLink = ref<string>();
const signPlacementOngoing = computed(() => loadingStore.isLoading("sign-placement"));
const signModeChoiceOpen = ref<boolean>(false);
const signSuccess = ref<boolean>(false);
const signPhase = ref<"idle" | "preparing" | "submitting">("idle");
const signing = computed(() => signPhase.value !== "idle");
let signAbortController: AbortController | undefined;
const signModeLocked = computed(() => signPhase.value === "submitting" || scws.chooseReaderModalProps.open);

const [addIcon] = useIcons(RiAddFill);

watch(
  () => userPrefStore.signatureObjects,
  async () => {
    if (userPrefStore.defaultSignature) {
      defaultSignatureLink.value = await userPrefStore.getSignatureLink(userPrefStore.defaultSignature.id);
    } else {
      defaultSignatureLink.value = undefined;
    }
  },
  { immediate: true, deep: true }
);

watch(signModeChoiceOpen, open => {
  if (!open && signPhase.value === "preparing") {
    signAbortController?.abort();
  }
});

function placeSignature() {
  signEvents.emit("placePending", userPrefStore.defaultSignature!.id);
}

function chooseSignMode() {
  if (!simpleFeatureStore.pades.enabled) {
    sign({ pades: false });
  } else {
    signModeChoiceOpen.value = true;
  }
}

async function sign({ pades }: { pades: boolean }) {
  if (!props.pendingSignatures.length) return;

  signAbortController = new AbortController();
  const { signal } = signAbortController;
  signPhase.value = "preparing";
  try {
    sentry.addBreadcrumb({ message: "sign:Save - start" });

    if (pades && !(await prepareCertifiedSignature(scws, signal))) {
      return;
    }

    signPhase.value = "submitting";
    sentry.addBreadcrumb({ message: "sign:Save - sign" });
    await documentApi.sign(props.idDocToSign, letterFile.value!.id, props.pendingSignatures, pades);

    notifySignSuccess();
    signSuccess.value = true;
  } catch (e) {
    if (signal.aborted) return;

    notifySignFailure({ pades });
    sentry.captureException(e as Error);
  } finally {
    await logoffSafely(scws);
    signPhase.value = "idle";
    signModeChoiceOpen.value = false;
  }

  if (signSuccess.value) {
    router.push({
      name: "form-update",
      params: { chrono: letterFile.value!.chronoNumber },
    });
  }
}

/** Returns whether the certified signature device is ready; throws an AbortError if cancelled meanwhile. */
async function prepareCertifiedSignature(scws: SCWSApi, signal: AbortSignal): Promise<boolean> {
  sentry.addBreadcrumb({ message: "sign:Save - scws init" });
  await scws.init(signal);
  signal.throwIfAborted();

  sentry.addBreadcrumb({ message: "sign:Save - scws pin" });
  const unlocked = await scws.unlockPinForSignature(signal);
  signal.throwIfAborted();

  return !!unlocked;
}

function notifySignSuccess() {
  const pages = uniq(props.pendingSignatures.map(item => item.coordinates.pageIndex + 1).sort());
  const plural = pages.length > 1 ? "s" : "";
  alertManager.createAlert({
    message: `Document signé avec succès. Page${plural} affectée${plural} : ${pages.join(", ")}`,
    type: AlertType.SUCCESS,
    duration: AlertDuration.MEDIUM,
  });
}

function notifySignFailure({ pades }: { pades: boolean }) {
  const alert: Alert = {
    message: "La signature n'a pas pu avoir lieu. Veuillez réessayer ultérieurement.",
    type: AlertType.WARNING,
    duration: AlertDuration.LONG * 2,
  };
  if (pades) {
    alert.message =
      "La signature n'a pas pu avoir lieu. Vérifiez que le périphérique de signature est correctement branché";
    alert.link = {
      to: "/sign-help",
      text: "Page d'aide à la signature certifiée",
    };
  }

  alertManager.createAlert(alert);
}

async function logoffSafely(scws: SCWSApi) {
  sentry.addBreadcrumb({ message: "sign:Save - scws logoff" });
  try {
    await scws.logoff();
  } catch (e) {
    sentry.captureException(e as Error);
  }
}

onBeforeRouteLeave(() => {
  if (props.pendingSignatures.length > 0 && !signSuccess.value) {
    const confirmText = replacePlural(
      props.pendingSignatures.length,
      "La signature apposée sera perdue.",
      "Les signatures apposées seront perdues."
    );
    if (!window.confirm(confirmText + " Continuer ?")) {
      useSentry().addBreadcrumb({
        message: "canceled navigation because unsubmitted pending signature(s)",
      });
      return false;
    }
  }

  return true;
});
</script>
